import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Spin,
  Table,
  Progress,
  Typography,
} from "antd";
import {
  UserOutlined,
  BankOutlined,
  DashboardOutlined,
  TrophyOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import apiClient from "../utils/apiClient";

const { Title } = Typography;

const SuperAdminAnalytics = () => {
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/superadmin/analytics");
      setAnalytics(response.data?.data || {});
    } catch (error) {
      console.error("Failed to fetch analytics");
      // Set mock data for display
      setAnalytics({
        totalCompanies: 12,
        totalUsers: 156,
        activeSessions: 23,
        verifiedUsers: 134,
        pendingUsers: 22,
        systemUptime: 99.8,
      });
    } finally {
      setLoading(false);
    }
  };

  const companyData = [
    {
      key: "1",
      name: "Travel Pro Ltd",
      users: 25,
      status: "Active",
      revenue: "$45,000",
    },
    {
      key: "2",
      name: "Adventure Tours",
      users: 18,
      status: "Active",
      revenue: "$32,000",
    },
    {
      key: "3",
      name: "City Breaks Co",
      users: 12,
      status: "Active",
      revenue: "$28,000",
    },
    {
      key: "4",
      name: "Luxury Escapes",
      users: 8,
      status: "Pending",
      revenue: "$15,000",
    },
  ];

  const companyColumns = [
    {
      title: "Company Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Users",
      dataIndex: "users",
      key: "users",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span style={{ color: status === "Active" ? "#52c41a" : "#faad14" }}>
          {status}
        </span>
      ),
    },
    {
      title: "Revenue",
      dataIndex: "revenue",
      key: "revenue",
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Title level={2}>System Analytics</Title>

      {/* Main Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Companies"
              value={analytics.totalCompanies || 0}
              prefix={<BankOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Users"
              value={analytics.totalUsers || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Sessions"
              value={analytics.activeSessions || 0}
              prefix={<DashboardOutlined />}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="System Uptime"
              value={analytics.systemUptime || 99.9}
              suffix="%"
              prefix={<TrophyOutlined />}
              valueStyle={{ color: "#f5222d" }}
            />
          </Card>
        </Col>
      </Row>

      {/* User Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card title="User Verification Status">
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="Verified Users"
                  value={analytics.verifiedUsers || 0}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Pending Verification"
                  value={analytics.pendingUsers || 0}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: "#faad14" }}
                />
              </Col>
            </Row>
            <div style={{ marginTop: 16 }}>
              <Progress
                percent={Math.round(
                  ((analytics.verifiedUsers || 0) /
                    (analytics.totalUsers || 1)) *
                    100,
                )}
                status="active"
                strokeColor="#52c41a"
              />
            </div>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="System Health">
            <div style={{ textAlign: "center", padding: "20px" }}>
              <CheckCircleOutlined
                style={{ fontSize: "48px", color: "#52c41a" }}
              />
              <p style={{ marginTop: "16px", color: "#666", fontSize: "16px" }}>
                All Systems Operational
              </p>
              <p style={{ color: "#999" }}>
                Last checked: {new Date().toLocaleString()}
              </p>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Company Performance Table */}
      <Row gutter={16}>
        <Col span={24}>
          <Card title="Top Performing Companies">
            <Table
              columns={companyColumns}
              dataSource={companyData}
              pagination={false}
              size="middle"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SuperAdminAnalytics;
